<p-dialog [visible]="visible" (visibleChange)="visibleChange.emit($event)" [modal]="true" [draggable]="false"
    [resizable]="false" [style]="{ width: 'min(1480px, 96vw)' }"
    [contentStyle]="{ height: 'calc(100vh - 11rem)', overflow: 'auto' }" styleClass="fleet-onboarding-dialog"
    header="AI Fleet Onboarding">
    <section class="fleet-onboarding-layout">
        <aside class="fleet-onboarding-summary">
            <div class="summary-icon"><i class="pi pi-sparkles"></i></div>
            <small>Driver + vehicle</small>
            <h3>One guided onboarding flow</h3>
            <p>Read the Iqama, licence, and Istimara once, review the extracted details, then save and pair the driver
                and vehicle together.</p>
            <div class="summary-facts">
                <div><span>Step 1</span><strong>Upload documents</strong></div>
                <div><span>Step 2</span><strong>AI extracts the details</strong></div>
                <div><span>Step 3</span><strong>Save and pair once</strong></div>
            </div>
        </aside>

        <form [formGroup]="form" class="fleet-onboarding-form">
            <div class="fleet-onboarding-toolbar">
                <div>
                    <small>Fleet source</small>
                    <strong>How should these resources be classified?</strong>
                </div>
                <p-selectButton [options]="sourceOptions" [(ngModel)]="source" [ngModelOptions]="{ standalone: true }"
                    optionLabel="label" optionValue="value" (ngModelChange)="onSourceChange($event)"
                    styleClass="fleet-source-select">
                    <ng-template #item let-option>
                        <span class="fleet-source-option"><i [class]="option.icon"></i>{{ option.label }}</span>
                    </ng-template>
                </p-selectButton>
            </div>

            @if (isTransporter) {
            <div class="fleet-transporter-selection">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select inputId="fleetTransporter" [options]="transporterOptions"
                            formControlName="transporterCrmClientId" optionLabel="transporterName"
                            optionValue="crmClientId" [filter]="true"
                            filterBy="transporterName,crmClientNumber,linkedCompanyName,transportationAgencyName"
                            [loading]="transporterOptionsLoading" appendTo="body" styleClass="w-full">
                        </p-select>
                        <label for="fleetTransporter">Transporter *</label>
                    </p-iftalabel>
                </p-inputGroup>
                <small>The new driver and vehicle will be owned by this transporter and linked together.</small>
            </div>
            }

            <section class="onboarding-stage">
                <div class="stage-heading">
                    <span class="stage-number">1</span>
                    <div>
                        <h4>Upload source documents</h4>
                        <p>Use clear images. AI will prefill the review fields below.</p>
                    </div>
                    <p-button label="Extract with AI" icon="pi pi-sparkles" [disabled]="!hasAllDocuments"
                        [loading]="extracting" (click)="extract()"></p-button>
                </div>
                <div class="upload-mode-bar">
                    <p-selectButton [options]="uploadModeOptions" [(ngModel)]="documentMode"
                        [ngModelOptions]="{ standalone: true }" optionLabel="label" optionValue="value"
                        (ngModelChange)="setDocumentMode($event)" styleClass="upload-mode-select">
                        <ng-template #item let-option>
                            <span class="upload-mode-option"><i [class]="option.icon"></i>{{ option.label }}</span>
                        </ng-template>
                    </p-selectButton>
                    <small class="upload-mode-hint">{{ isCombinedMode
                        ? 'Upload one image containing all three cards. AI splits and reads each document.'
                        : 'Upload each document as a separate image for the most accurate extraction.' }}</small>
                </div>
                @if (isCombinedMode) {
                <div class="document-grid single">
                    <article [class.ready]="combinedFile"><i class="pi pi-images"></i>
                        <div><strong>Combined Iqama + Licence + Istimara</strong><span>{{ combinedFile?.name ||
                                'One image with all three documents stacked or side by side' }}</span></div>
                        <p-fileUpload mode="basic" chooseLabel="Choose image" accept="image/*" [auto]="false"
                            (onSelect)="selectDocument('combined', $event)"></p-fileUpload>
                    </article>
                </div>
                } @else {
                <div class="document-grid">
                    <article [class.ready]="residenceFile"><i class="pi pi-id-card"></i>
                        <div><strong>Iqama / residence ID</strong><span>{{ residenceFile?.name || 'Required image'
                                }}</span></div><p-fileUpload mode="basic" chooseLabel="Choose image" accept="image/*"
                            [auto]="false" (onSelect)="selectDocument('residence', $event)"></p-fileUpload>
                    </article>
                    <article [class.ready]="licenseFile"><i class="pi pi-car"></i>
                        <div><strong>Driver licence</strong><span>{{ licenseFile?.name || 'Required image' }}</span>
                        </div><p-fileUpload mode="basic" chooseLabel="Choose image" accept="image/*" [auto]="false"
                            (onSelect)="selectDocument('license', $event)"></p-fileUpload>
                    </article>
                    <article [class.ready]="registrationFile"><i class="pi pi-truck"></i>
                        <div><strong>Vehicle registration</strong><span>{{ registrationFile?.name || 'Required image'
                                }}</span></div><p-fileUpload mode="basic" chooseLabel="Choose image" accept="image/*"
                            [auto]="false" (onSelect)="selectDocument('registration', $event)"></p-fileUpload>
                    </article>
                </div>
                }
            </section>

            <section class="onboarding-stage review-stage">
                <div class="stage-heading"><span class="stage-number">2</span>
                    <div>
                        <h4>Review extracted information</h4>
                        <p>{{ extracted ? 'AI values are ready. Correct anything before saving.' : 'You can enter
                            values manually, or extract them from all three images.' }}</p>
                    </div><p-tag [value]="extracted ? 'AI extracted' : 'Awaiting extraction'"
                        [severity]="extracted ? 'success' : 'secondary'"></p-tag>
                </div>
                @if (lowConfidenceFields.size > 0) {
                <div class="low-confidence-banner">
                    <i class="pi pi-exclamation-triangle"></i>
                    <span>{{ lowConfidenceFields.size }} field(s) marked with a warning have low AI confidence —
                        please verify them before saving the driver and vehicle.</span>
                </div>
                }
                @if (errorMessage) { <div class="onboarding-error"><i class="pi pi-exclamation-circle"></i>{{
                    errorMessage }}</div> }
                <div class="review-columns">
                    <div class="review-card">
                        <h5><i class="pi pi-user"></i> Driver</h5>
                        <div class="field-grid">
                            <p-inputGroup class="wide"><p-inputGroupAddon><i
                                        class="pi pi-user"></i></p-inputGroupAddon><p-iftalabel><input
                                        id="fleetDriverName" pInputText formControlName="name" /><label
                                        for="fleetDriverName">Driver legal name *</label></p-iftalabel>
                                @if (isLowConfidence('name')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-phone"></i></p-inputGroupAddon><p-iftalabel><input
                                        id="fleetDriverPhone" pInputText formControlName="phoneNumber" /><label
                                        for="fleetDriverPhone">Mobile number *</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-id-card"></i></p-inputGroupAddon><p-iftalabel><input
                                        id="fleetResidence" pInputText formControlName="residenceNumber" /><label
                                        for="fleetResidence">Residence number *</label></p-iftalabel>
                                @if (isLowConfidence('residenceNumber')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-calendar"></i></p-inputGroupAddon><p-iftalabel><p-datePicker
                                        inputId="fleetResidenceExpiry" formControlName="residenceExpiryDate"
                                        dateFormat="yy-mm-dd" [showIcon]="true" appendTo="body"></p-datePicker><label
                                        for="fleetResidenceExpiry">Residence expiry</label></p-iftalabel>
                                @if (isLowConfidence('residenceExpiryDate')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-car"></i></p-inputGroupAddon><p-iftalabel><input id="fleetLicence"
                                        pInputText formControlName="licenseNumber" /><label for="fleetLicence">Licence
                                        number *</label></p-iftalabel>
                                @if (isLowConfidence('licenseNumber')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-calendar-clock"></i></p-inputGroupAddon><p-iftalabel><p-datePicker
                                        inputId="fleetLicenceExpiry" formControlName="licenseExpiryDate"
                                        dateFormat="yy-mm-dd" [showIcon]="true" appendTo="body"></p-datePicker><label
                                        for="fleetLicenceExpiry">Licence expiry</label></p-iftalabel>
                                @if (isLowConfidence('licenseExpiryDate')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-flag"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetNationality" [options]="countries" optionLabel="name"
                                        formControlName="nationality" [filter]="true" appendTo="body"></p-select><label
                                        for="fleetNationality">Nationality</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-map-marker"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetCountry" [options]="countries" optionLabel="name"
                                        formControlName="residingCountry" [filter]="true"
                                        appendTo="body" (onChange)="onResidingCountryChange()"></p-select><label for="fleetCountry">Residing country
                                        *</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-map"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetResidingCity" [options]="residingCities" optionLabel="cityName"
                                        formControlName="residingCity" [filter]="true" appendTo="body"></p-select><label
                                        for="fleetResidingCity">Residing city *</label></p-iftalabel></p-inputGroup>
                        </div>
                    </div>
                    <div class="review-card">
                        <h5><i class="pi pi-truck"></i> Vehicle</h5>
                        <div class="field-grid">
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-hashtag"></i></p-inputGroupAddon><p-iftalabel><input
                                        id="fleetPlate" pInputText formControlName="plateNumber" /><label
                                        for="fleetPlate">Plate number *</label></p-iftalabel>
                                @if (isLowConfidence('plateNumber')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-ticket"></i></p-inputGroupAddon><p-iftalabel><input
                                        id="fleetRegistration" pInputText formControlName="registrationNumber" /><label
                                        for="fleetRegistration">Registration number *</label></p-iftalabel>
                                @if (isLowConfidence('registrationNumber')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-cog"></i></p-inputGroupAddon><p-iftalabel><input id="fleetEngine"
                                        pInputText formControlName="engineNumber" /><label for="fleetEngine">Engine
                                        number</label></p-iftalabel>
                                @if (isLowConfidence('engineNumber')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-key"></i></p-inputGroupAddon><p-iftalabel><input id="fleetChassis"
                                        pInputText formControlName="chassisNumber" /><label for="fleetChassis">Chassis / VIN
                                        (optional)</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-calendar"></i></p-inputGroupAddon><p-iftalabel><p-datePicker
                                        inputId="fleetRegistrationExpiry" formControlName="registrationExpiry"
                                        dateFormat="yy-mm-dd" [showIcon]="true" appendTo="body"></p-datePicker><label
                                        for="fleetRegistrationExpiry">Registration
                                        expiry</label></p-iftalabel>
                                @if (isLowConfidence('registrationExpiry')) { <i class="pi pi-exclamation-triangle low-confidence-icon"
                                    title="AI confidence is low — please verify"></i> }
                            </p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-truck"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetType" [options]="vehicleTypes" optionLabel="name"
                                        formControlName="vehicleType" [filter]="true" appendTo="body"
                                        (onChange)="onVehicleTypeChange()"></p-select><label for="fleetType">Vehicle
                                        type *</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-sliders-h"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetConfig" [options]="vehicleConfigs" optionLabel="name"
                                        formControlName="vehicleConfig" appendTo="body"></p-select><label
                                        for="fleetConfig">Configuration *</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-weight-hanging"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetCapacity" [options]="vehicleCapacities" optionLabel="name"
                                        formControlName="vehicleCapacity" appendTo="body"></p-select><label
                                        for="fleetCapacity">Capacity *</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-car"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetMake" [options]="vehicleMakes" optionLabel="name"
                                        formControlName="vehicleMake" [filter]="true" appendTo="body"
                                        (onChange)="onVehicleMakeChange()"></p-select><label
                                        for="fleetMake">Make</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-directions"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetModel" [options]="vehicleModels" optionLabel="name"
                                        formControlName="vehicleModel" appendTo="body"
                                        (onChange)="onVehicleModelChange()"></p-select><label
                                        for="fleetModel">Model</label></p-iftalabel></p-inputGroup>
                            <p-inputGroup><p-inputGroupAddon><i
                                        class="pi pi-calendar"></i></p-inputGroupAddon><p-iftalabel><p-select
                                        inputId="fleetYear" [options]="vehicleYears" optionLabel="year"
                                        formControlName="vehicleYear" appendTo="body"></p-select><label
                                        for="fleetYear">Year</label></p-iftalabel></p-inputGroup>
                        </div>
                    </div>
                </div>
                @if (isRental) {
                <div class="rental-card">
                    <div class="rental-card-heading">
                        <div>
                            <h5><i class="pi pi-file-edit"></i> Rental agreement</h5>
                            <p>Select the agreement that owns the supplier, dates, daily rates, and commercial rules.
                            </p>
                        </div>
                        <p-button label="Create rental agreement" icon="pi pi-plus" severity="secondary"
                            [outlined]="true" size="small" (onClick)="createRentalAgreement()"></p-button>
                    </div>
                    <div class="field-grid">
                        <p-inputGroup class="wide">
                            <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select inputId="fleetRentalAgreement" [options]="rentalAgreementOptions"
                                    optionLabel="label" optionValue="value" formControlName="rentalAgreementId"
                                    [filter]="true" [loading]="rentalAgreementsLoading" [showClear]="true"
                                    appendTo="body"
                                    emptyMessage="No rental agreements are available"></p-select>
                                <label for="fleetRentalAgreement">Rental agreement *</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <p-tag
                        value="The driver and vehicle will be linked to this agreement. Rates remain managed on the agreement."
                        severity="info"></p-tag>
                </div>
                }
            </section>
        </form>
    </section>
    <ng-template pTemplate="footer">
        <div class="fleet-onboarding-footer"><span>{{ source === 'freelance' || isRental
                ? 'Creates new resources or links matching existing resources for this company, then pairs them.'
                : 'Creates a driver and vehicle, then pairs them.' }}</span>
            <div><p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true"
                    (click)="close()"></p-button><p-button [label]="source === 'freelance' || isRental ? 'Create or link driver and vehicle' : 'Create and link driver and vehicle'" icon="pi pi-link"
                    [loading]="saving" (click)="save()"></p-button></div>
        </div>
    </ng-template>
</p-dialog>
